/**
 * Board Component
 *
 * Root game board container with exact layout per spec.
 * Board: 1000x968px with three blocks: Banner, Main (grid), Footer
 * Scales between 40% and 100% based on viewport size
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useGameContext } from '../contexts/GameContext';
import { useBoardLayout, BOARD_WIDTH, BOARD_HEIGHT } from '../hooks/useBoardLayout';
import { useBannerAnimation, ROTATE_DURATION as _RD } from '../hooks/useBannerAnimation';
// DEBUG: apply speed multiplier to banner animation durations (remove when DEBUG_SPEED = 1)
import { registerSolveComplete, registerLockoutComplete } from './grid/ClueCell';
import ColumnContainer from './grid/ColumnContainer';
import ColumnNumberLabel from './grid/ColumnNumberLabel';
import BoardBackground from '../svg/BoardBackground';
import LogoFull from '../svg/LogoFull';
import LogoLeft from '../svg/LogoLeft';
import LogoRight from '../svg/LogoRight';
import PlusSign from '../svg/PlusSign';
import CreditCounterArea from './CreditCounterArea';
import AGameOfAnalogies from './AGameOfAnalogies';
import ExplanationBanner from './ExplanationBanner';
import RevealDoorFull from '../svg/RevealDoorFull';
import SvgRevealDoorLeft from '../svg/RevealDoorLeft';
import SvgRevealDoorRight from '../svg/RevealDoorRight';
import SolveButtonCell from './buttons/SolveButtonCell';
import SubmitDialog from './dialogs/SubmitDialog';
import WelcomePanel from './WelcomePanel';
import Header from './Header';
import PrismSlot, { PrismFace } from './PrismSlot';
import { createPrismFromSlot } from '../utils/prismGeometry';
import styles from './Board.module.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatPuzzleDisplayName(
  _name: string | undefined,
  idx: number | undefined,
  date: string,
): string {
  if (idx !== undefined) return `SAMPLE PUZZLE ${idx}`;
  return new Date(date + 'T12:00:00').toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

// Column x positions within mainBlock — where each 232px prism face starts.
// Column content sits at left:18 inside the face, aligning content at 18, 264, 510, 756.
const COLUMN_X_POSITIONS = [0, 246, 492, 738];

// DEBUG: multiply all open/close animation durations by this factor (set to 1 for production)
const DEBUG_SPEED = 1;
const ROTATE_DURATION = _RD * DEBUG_SPEED;

// Single duration for ALL synchronized board open/close animations
// (column rotation, A GAME doors, logo slide, WHO WHAT doors)
const BOARD_ANIM_DURATION = 1000 * DEBUG_SPEED;
// Door split uses same duration for sync
const DOOR_SPLIT_DURATION = BOARD_ANIM_DURATION;

// Column prism geometry — equilateral triangular prism, Y-axis rotation.
// Face width = slot width (232px). Prism rotates in NEGATIVE Y: 0° → -120°.
const COL = createPrismFromSlot('vert', 232, 620);

// Opening sequence timings
const COL_OPEN_DURATION = BOARD_ANIM_DURATION;      // each column rotation from Blank→Game
const RISE_TO_ROTATE_DELAY = 750 * DEBUG_SPEED;    // columns rotate just as WelcomePanel clears view

const showColumnGuess = (activeDialog: string, activeDialogColumn: number | null, activeDialogRow: number | null) =>
  activeDialog === 'column-guess' && activeDialogColumn !== null && activeDialogRow !== null;

interface BoardProps {
  onMain?: () => void;
  /** Called when user selects a different puzzle from within WelcomePanel */
  onSelectPuzzle?: (puzzle: { id: string; name: string; puzzleNumber?: number | null; puzzleText: string; puzzleJson?: import('../types').PuzzleJSON | null; date: string; sampleIndex?: number }) => void;
  puzzleName?: string;    // raw Supabase name e.g. "Sample 1"
  sampleIndex?: number;   // 1-based, present for sample games
  /** When true, immediately start the opening sequence on mount (seamless swap from LoginBoard) */
  autoStart?: boolean;
  /** Logout callback — closes board then signs out */
  onLogout?: () => void;
}

function Board({ onMain: _onMain, onSelectPuzzle, puzzleName, sampleIndex, autoStart, onLogout }: BoardProps = {}) {
  const {
    puzzle,
    credits,
    solvedColumns,
    activeDialog,
    activeDialogColumn,
    activeDialogRow,
    finalAnswerSubmitted,
    finalScore,
    gameWon,
    submitColumnGuess,
    submitFinalAnswer,
    openDialog,
    closeDialog,
    lockoutAnimating,
    clearLockout,
    resetGame,
    endGameCreditsExhausted,
    guessAttempts,
  } = useGameContext();
  const { scale, left, top } = useBoardLayout();
  const banner = useBannerAnimation();

  // Derive dialog state early so hooks below can reference it
  const isColumnGuessActive = showColumnGuess(activeDialog, activeDialogColumn, activeDialogRow);
  const isFinalAnswerActive = !finalAnswerSubmitted && activeDialog === 'final-answer';
  const isDialogOverBanner = isColumnGuessActive || isFinalAnswerActive;

  // Track whether the game is finished (final answer submitted).
  // Initialized from context; updated when finalAnswerSubmitted changes during play.
  // Used by handleStart to choose review vs. normal opening path.
  const gameAlreadyFinished = useRef(finalAnswerSubmitted);
  useEffect(() => {
    if (finalAnswerSubmitted) {
      gameAlreadyFinished.current = true;
    }
  }, [finalAnswerSubmitted]);

  // Explanation panel state — shows after final answer reveal
  const [explanationOpen, setExplanationOpen] = useState(false);


  // Door split state — reveal door splits to show SolveButton
  const shouldDoorBeOpen = !finalAnswerSubmitted && solvedColumns.length === 4;
  const [doorOpen, setDoorOpen] = useState(shouldDoorBeOpen);
  // doorSlideReady always starts false — split animation fires when boardOpen=true
  const [doorSlideReady, setDoorSlideReady] = useState(false);
  const [doorFullyOpen, setDoorFullyOpen] = useState(false);
  // doorHalvesClosed: true when split halves have finished animating back to center
  // Used to seamlessly swap from split halves → full door during close sequence
  const [doorHalvesClosed, setDoorHalvesClosed] = useState(false);
  // doorCancelPhase: drives cancel → pop-back → doors close → auto-reopen sequence
  const [doorCancelPhase, setDoorCancelPhase] = useState<'idle' | 'popping-back' | 'closing' | 'closed'>('idle');
  // triggerReveal starts true for returning complete players (solution face already visible)
  const [triggerReveal, setTriggerReveal] = useState(finalAnswerSubmitted);
  // boardOpen: false until player clicks Play/Resume
  const [boardOpen, setBoardOpen] = useState(false);
  // aGameDoorsOpen: delayed until glow sequence completes (normal game) or immediate (review)
  const [aGameDoorsOpen, setAGameDoorsOpen] = useState(false);
  // openPhase: drives the board opening animation when Play/Resume is clicked
  // 'closed'   — WelcomePanel fully visible, columns on Blank face
  // 'opening'  — CSS transitions active: WelcomePanel rises, columns rotate in
  // 'open'     — animation complete, WelcomePanel unmounted
  const [openPhase, setOpenPhase] = useState<'closed' | 'opening' | 'open'>('closed');
  // Welcome panel rise phase
  const [risePhase, setRisePhase] = useState<'idle' | 'rising' | 'risen' | 'dropping'>('idle');
  // Opening glow — brief highlight on each column's Game face backing after rotation.
  // -1 = off, 0-3 = that column, 4 = all, 5 = done
  const [openGlowCol, setOpenGlowCol] = useState(-1);
  // Welcome panel shows whenever the board hasn't been opened yet
  const showWelcomePanel = openPhase !== 'open';

  // Column prism state — per-column rotation target angles (negative Y rotation).
  // 0° = Blank face (resting state)
  // -120° = Game face (interactive column / solution view)
  const [colTargets, setColTargets] = useState<[number,number,number,number]>([0, 0, 0, 0]);

  // Board closing sequence — blocks interactive elements during simultaneous close animation
  const [boardClosing, setBoardClosing] = useState(false);
  // Callback to invoke when board close sequence fully completes (after plus un-rotation)
  const closeCompleteRef = useRef<(() => void) | null>(null);

  // Persist dialog visibility during banner close animation
  const [persistedVariant, setPersistedVariant] = useState<'column' | 'final' | null>(null);

  // Celebration sequence — gold text blink on green lozenges after all 4 columns solved
  // -1 = inactive, 0-3 = that column flashing gold, 4 = all gold (unison), 5 = done
  const [celebrationCol, setCelebrationCol] = useState(-1);
  const celebrationDoneRef = useRef(shouldDoorBeOpen || finalAnswerSubmitted);

  // Credits exhausted with unsolved columns → end game as loss (after lockout completes)
  useEffect(() => {
    if (!finalAnswerSubmitted && activeDialog === 'none' && !lockoutAnimating && !doorOpen) {
      if (credits === 0 && solvedColumns.length < 4) {
        endGameCreditsExhausted();
      }
    }
  }, [solvedColumns, credits, finalAnswerSubmitted, activeDialog, lockoutAnimating, doorOpen, endGameCreditsExhausted]);

  // Credits-exhausted: after endGameCreditsExhausted fires, open the door for solve prism reveal
  useEffect(() => {
    if (finalAnswerSubmitted && !doorOpen && openPhase === 'open' && solvedColumns.length < 4) {
      setDoorOpen(true);
    }
  }, [finalAnswerSubmitted, doorOpen, openPhase, solvedColumns.length]);

  // Clear lockout when all cell animations finish (signal-driven)
  useEffect(() => {
    if (lockoutAnimating) {
      registerLockoutComplete(clearLockout);
      return () => registerLockoutComplete(null);
    }
  }, [lockoutAnimating, clearLockout]);

  // Start celebration when all 4 columns solved (wait for all solve animations to complete)
  useEffect(() => {
    if (
      solvedColumns.length === 4 && !finalAnswerSubmitted && celebrationCol === -1
      && !doorOpen && activeDialog === 'none' && !lockoutAnimating
      && !celebrationDoneRef.current
    ) {
      console.log('[DOOR-DEBUG] Registering solveComplete callback');
      registerSolveComplete(() => { console.log('[DOOR-DEBUG] solveComplete fired → setCelebrationCol(0)'); setCelebrationCol(0); });
      return () => registerSolveComplete(null);
    } else if (solvedColumns.length === 4 && !finalAnswerSubmitted && celebrationCol === -1) {
      console.log('[DOOR-DEBUG] 4 cols solved but conditions not met:', { doorOpen, activeDialog, lockoutAnimating, celebrationDone: celebrationDoneRef.current });
    }
  }, [solvedColumns.length, finalAnswerSubmitted, celebrationCol, doorOpen, activeDialog, lockoutAnimating]);

  // Advance celebration: blink each column, all-gold unison, revert, then open door
  // Cadence mirrors the opening glow sequence (250ms each, 400ms pause, 500ms unison)
  useEffect(() => {
    if (celebrationCol >= 0 && celebrationCol < 4) {
      // Flash this column gold for 250ms, then advance
      const timer = setTimeout(() => setCelebrationCol(c => c + 1), 250);
      return () => clearTimeout(timer);
    }
    if (celebrationCol === 4) {
      // Pause before unison flash (matches glow pause)
      const timer = setTimeout(() => setCelebrationCol(5), 400);
      return () => clearTimeout(timer);
    }
    if (celebrationCol === 5) {
      // All-gold unison — hold for 500ms
      const timer = setTimeout(() => setCelebrationCol(6), 500);
      return () => clearTimeout(timer);
    }
    if (celebrationCol === 6) {
      // Celebration complete — revert text to normal, pause then open door
      celebrationDoneRef.current = true;
      console.log('[DOOR-DEBUG] celebrationCol=6 → scheduling setDoorOpen(true) in 400ms');
      const timer = setTimeout(() => { console.log('[DOOR-DEBUG] setDoorOpen(true) firing'); setDoorOpen(true); }, 400);
      return () => clearTimeout(timer);
    }
  }, [celebrationCol]);

  // Drive banner animation from dialog state or explanation panel.
  // Skipped during board open/close sequences (those drive the banner directly).
  useEffect(() => {
    if (boardClosing || openPhase === 'opening') return;
    if (explanationOpen) {
      banner.openFull();
    } else if (isDialogOverBanner) {
      banner.open();
    } else {
      const timer = setTimeout(() => banner.close(), 200);
      return () => clearTimeout(timer);
    }
  }, [isDialogOverBanner, explanationOpen, banner.open, banner.openFull, banner.close, boardClosing, openPhase]);

  // Track which dialog was active (for persistence during banner close)
  useEffect(() => {
    if (isColumnGuessActive) setPersistedVariant('column');
    else if (isFinalAnswerActive) setPersistedVariant('final');
  }, [isColumnGuessActive, isFinalAnswerActive]);

  // Clear persisted variant when banner fully closes
  useEffect(() => {
    if (banner.phase === 'idle') {
      setPersistedVariant(null);
    }
  }, [banner.phase]);

  // Board close complete — called when all close animations are done.
  // Reset to closed state, drop WelcomePanel back in, invoke onComplete callback.
  const finishClose = useCallback(() => {
    banner.setSlideDuration(null);
    setRisePhase('risen');
    setOpenPhase('closed');
    setBoardClosing(false);
    setDoorOpen(false);
    setDoorHalvesClosed(false);
    setTriggerEndGameAnim(false);

    const onComplete = closeCompleteRef.current;
    closeCompleteRef.current = null;
    if (onComplete) onComplete();

    // Brief pause before WelcomePanel drops in
    setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setRisePhase('dropping');
        });
      });
    }, 400 * DEBUG_SPEED);
  }, [banner.setSlideDuration]);

  // When banner reaches idle during a close sequence, the close is complete
  // (banner was open → sliding-closed → unrotating → idle)
  const bannerWasClosing = useRef(false);
  useEffect(() => {
    if (boardClosing && (banner.phase === 'sliding-closed' || banner.phase === 'unrotating')) {
      bannerWasClosing.current = true;
    }
    if (boardClosing && banner.phase === 'idle' && bannerWasClosing.current) {
      bannerWasClosing.current = false;
      finishClose();
    }
  }, [boardClosing, banner.phase, finishClose]);

  // Door split animation — double-rAF to trigger CSS transition.
  // Gated on openPhase='open' so the split doesn't fire during the opening animation
  // (which would conflict for returning players who already have doorOpen=true).
  useEffect(() => {
    if (openPhase === 'open' && boardOpen && doorOpen && !doorSlideReady && doorCancelPhase === 'idle') {
      console.log('[DOOR-DEBUG] Door split effect firing → double-rAF → setDoorSlideReady(true)');
      setDoorHalvesClosed(false);
      let cancelled = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) { console.log('[DOOR-DEBUG] setDoorSlideReady(true)'); setDoorSlideReady(true); }
        });
      });
      return () => { cancelled = true; };
    } else if (doorOpen) {
      console.log('[DOOR-DEBUG] doorOpen=true but split not firing:', { openPhase, boardOpen, doorSlideReady, doorCancelPhase });
    }
  }, [openPhase, boardOpen, doorOpen, doorSlideReady, doorCancelPhase]);

  // Cancel sequence: after pop-back animation starts, close the doors
  useEffect(() => {
    if (doorCancelPhase !== 'popping-back') return;
    const t = setTimeout(() => {
      setDoorSlideReady(false);
      setDoorCancelPhase('closing');
    }, 350);
    return () => clearTimeout(t);
  }, [doorCancelPhase]);

  // Cancel sequence: after doors finish closing, wait then auto-reopen
  useEffect(() => {
    if (doorCancelPhase !== 'closed') return;
    const t = setTimeout(() => setDoorCancelPhase('idle'), 600);
    return () => clearTimeout(t);
  }, [doorCancelPhase]);

  // When final answer submitted: animate door halves fully open
  // No rAF needed — the split animation already completed, so the browser's computed style
  // for the door panels is already at ±190px. Changing the transform target to ±380px with
  // the same transition duration will correctly fire a CSS transition from the current state.
  useEffect(() => {
    if (finalAnswerSubmitted && doorSlideReady && !doorFullyOpen) {
      setDoorFullyOpen(true);
    }
  }, [finalAnswerSubmitted, doorSlideReady, doorFullyOpen]);

  // Opening sequence
  useEffect(() => {
    if (openPhase !== 'opening') return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const isReview = gameAlreadyFinished.current;

    // WelcomePanel rises out of view
    timers.push(setTimeout(() => setRisePhase('risen'), 800));

    if (isReview) {
      // Review mode: plus rotates first, then everything else syncs.
      // Start banner (plus rotates → sliding-open → slide starts).
      // The slide and all other animations are triggered from banner phase effects below.
      banner.setSlideDuration(1000);
      timers.push(setTimeout(() => {
        banner.openFull();
        setExplanationOpen(true);
      }, RISE_TO_ROTATE_DELAY));
    } else {
      // Normal game: no banner, no WHO WHAT doors.
      // Columns + A GAME doors start simultaneously after WelcomePanel clears.
      timers.push(setTimeout(() => {
        setColTargets([-120, -120, -120, -120]);
        setAGameDoorsOpen(true);
      }, RISE_TO_ROTATE_DELAY));

      // Glow sequence after columns finish rotating
      const allRotated = RISE_TO_ROTATE_DELAY + COL_OPEN_DURATION;
      const GLOW_EACH = 250;
      const GLOW_PAUSE = 400;
      const GLOW_ALL = 500;
      for (let i = 0; i < 4; i++) {
        timers.push(setTimeout(() => setOpenGlowCol(i), allRotated + 100 + i * GLOW_EACH));
      }
      const fourOff = allRotated + 100 + 4 * GLOW_EACH;
      timers.push(setTimeout(() => setOpenGlowCol(-1), fourOff));
      const afterFour = fourOff + GLOW_PAUSE;
      timers.push(setTimeout(() => setOpenGlowCol(4), afterFour));
      timers.push(setTimeout(() => {
        setOpenGlowCol(5);
        setOpenPhase('open');
      }, afterFour + GLOW_ALL));
    }

    return () => timers.forEach(clearTimeout);
  }, [openPhase, banner.openFull, banner.setSlideDuration]);

  // Review opening — phase 2: when banner reaches 'sliding-open' (plus rotation done),
  // start all other animations simultaneously (columns, A GAME doors, WHO WHAT doors)
  useEffect(() => {
    if (openPhase !== 'opening' || banner.phase !== 'sliding-open') return;
    if (!gameAlreadyFinished.current) return; // only for review mode

    setColTargets([-120, -120, -120, -120]);
    setAGameDoorsOpen(true);
    setDoorHalvesClosed(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setDoorSlideReady(true);
        setDoorFullyOpen(true);
      });
    });
  }, [openPhase, banner.phase]);

  // Review opening — phase 3: when banner finishes sliding open, all synced animations
  // are also complete. Mark board open and reset banner slide duration.
  useEffect(() => {
    if (openPhase !== 'opening' || banner.phase !== 'open') return;
    if (!gameAlreadyFinished.current) return; // only for review mode

    banner.setSlideDuration(null);
    setOpenPhase('open');
  }, [openPhase, banner.phase, banner.setSlideDuration]);

  // Solve button click handler
  const handleSolveClick = () => {
    openDialog('final-answer');
  };

  // After solve prism finishes revealing: trigger end-game animation in credit counter area
  const [triggerEndGameAnim, setTriggerEndGameAnim] = useState(false);
  const handleRevealComplete = useCallback(() => {
    setTriggerEndGameAnim(true);
  }, []);



  // Close the board — ALL animations start simultaneously, then plus un-rotates after.
  // Optional onComplete callback runs after the full sequence (used by handleReset).
  const handleClose = useCallback((onComplete?: () => void) => {
    setBoardClosing(true);
    closeCompleteRef.current = onComplete ?? null;

    // Set banner slide duration to match all other board animations
    banner.setSlideDuration(1000);

    // ALL close animations start simultaneously (BOARD_ANIM_DURATION):
    setAGameDoorsOpen(false);         // A GAME doors close
    setBoardOpen(false);
    setColTargets([0, 0, 0, 0]);      // columns rotate to sparkle
    setExplanationOpen(false);         // (stays mounted during boardClosing if finalAnswerSubmitted)

    // Banner: if open/sliding, close() starts slide→unrotate→idle chain.
    // If already idle, use timeout to wait for other animations then finishClose.
    if (banner.phase !== 'idle') {
      banner.close();
    } else {
      setTimeout(() => finishClose(), BOARD_ANIM_DURATION + 50);
    }

    // WHO WHAT door halves close
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setDoorSlideReady(false);
        setDoorFullyOpen(false);
      });
    });
  }, [banner.close, banner.setSlideDuration, banner.phase, finishClose]);

  // Auto-close board 30 seconds after end-of-game animation completes
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (explanationOpen && finalAnswerSubmitted && !boardClosing) {
      autoCloseTimerRef.current = setTimeout(() => {
        handleClose();
      }, 30000);
      return () => {
        if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
      };
    }
  }, [explanationOpen, finalAnswerSubmitted, boardClosing, handleClose]);

  // Open board — kicks off the opening animation sequence
  const handleStart = () => {
    if (gameAlreadyFinished.current) {
      // Review mode: open board showing review face + solution
      setBoardOpen(true);
      setOpenPhase('opening');
      setRisePhase('rising');
      setDoorOpen(true);
      // Don't pre-open doors — let them animate in sync with everything else
      setTriggerEndGameAnim(true); // show final score immediately (no animation in review)
      return;
    }
    setBoardOpen(true);
    setOpenPhase('opening');
    setRisePhase('rising');
  };

  // Auto-start: trigger opening sequence once puzzle is loaded (seamless swap from LoginBoard).
  // Puzzle loads asynchronously in GameContext, so Board may render "Loading..." initially.
  // We wait for puzzle to be non-null before firing the animation.
  // Double-rAF ensures the browser has painted WelcomePanel at its idle position (translateY(0))
  // before the transition + target transform are applied, preventing a judder/skip.
  const autoStartFired = useRef(false);
  useEffect(() => {
    if (autoStart && puzzle && !autoStartFired.current) {
      autoStartFired.current = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          handleStart();
        });
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, puzzle]);

  // Reset game — plays close animation, then resets game state
  const handleReset = useCallback(() => {
    handleClose(() => {
      resetGame();
      setDoorCancelPhase('idle');
      setTriggerReveal(false);
      setOpenGlowCol(-1);
      setCelebrationCol(-1);
      celebrationDoneRef.current = false;
      gameAlreadyFinished.current = false;
    });
  }, [handleClose, resetGame]);

  // Final answer Back handler — close dialog, start pop-back → door-close sequence
  const handleFinalBack = () => {
    closeDialog();
    setDoorCancelPhase('popping-back');
  };

  // Dialog stays visible until logos fully close (persisted during banner close)
  const showColumnDialog = isColumnGuessActive || (persistedVariant === 'column' && banner.dialogVisible);
  const showFinalDialog = isFinalAnswerActive || (persistedVariant === 'final' && banner.dialogVisible);
  const showDialogBg = isDialogOverBanner || (persistedVariant !== null && banner.dialogVisible);


  return (
    <>
    <Header onReset={handleReset} onLogout={onLogout} />
    <div
      style={{
        position: 'fixed',
        left,
        top,
        width: BOARD_WIDTH * scale,
        height: BOARD_HEIGHT * scale,
        overflow: 'hidden',
        background: 'none',
        zIndex: 1,
      }}
    >
      <div
        style={{
          width: BOARD_WIDTH,
          height: BOARD_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          position: 'relative',
        }}
        className={styles.board}
      >
        {/* Board background - positioned behind all elements */}
        <BoardBackground />

        {/* Banner Block - Logo area (15, 15, 970, 170) */}
        <div className={styles.bannerBlock}>
          {/* Explanation Banner — visible when logos fully part after game end.
              Stays mounted during boardClosing so the banner logos close OVER it. */}
          {(explanationOpen || (boardClosing && finalAnswerSubmitted)) && (
            <div style={{
              position: 'absolute', left: 3, top: 3,
              width: 964, height: 164, zIndex: 96,
              pointerEvents: 'none',
            }}>
              <ExplanationBanner />
            </div>
          )}

          {/* Solid brown background — behind dialog and logos */}
          {showDialogBg && (
            <div style={{
              position: 'absolute',
              left: 3,
              top: 3,
              width: 964,
              height: 164,
              zIndex: 95,
              pointerEvents: 'none',
              backgroundColor: '#514b44',
            }} />
          )}

          {/* Left logo clip container — trapezoid clip hides edge strip when closed.
               Clip: (0,0)→(564,0)→(400,164)→(0,164). Logo slides left within fixed clip. */}
          <div style={{
            position: 'absolute', left: 3, top: 3, width: 564, height: 164,
            clipPath: 'polygon(0 0, 564px 0, 400px 164px, 0 164px)',
            overflow: 'hidden',
            zIndex: 110,
            pointerEvents: 'none',
          }}>
            <div
              style={{
                position: 'relative',
                width: 568, height: 164,
                transform: `translateX(${-banner.slideOffset}px)`,
                transition: banner.slideTransitioning
                  ? `transform ${banner.slideDuration * DEBUG_SPEED}ms ease` : 'none',
              }}
              onTransitionEnd={(e) => {
                if (e.propertyName === 'transform' && e.target === e.currentTarget)
                  banner.onSlideTransitionEnd();
              }}
            >
              <LogoLeft />
            </div>
          </div>

          {/* Right logo clip container — trapezoid clip hides edge strip when closed.
               Clip: (164,0)→(564,0)→(564,164)→(0,164). Logo slides right within fixed clip. */}
          <div style={{
            position: 'absolute', left: 403, top: 3, width: 564, height: 164,
            clipPath: 'polygon(164px 0, 564px 0, 564px 164px, 0 164px)',
            overflow: 'hidden',
            zIndex: 110,
            pointerEvents: 'none',
          }}>
            <div style={{
              position: 'relative',
              width: 568, height: 164,
              transform: `translateX(${-4 + banner.slideOffset}px)`,
              transition: banner.slideTransitioning
                ? `transform ${banner.slideDuration * DEBUG_SPEED}ms ease` : 'none',
            }}>
              <LogoRight />
            </div>
          </div>

          {/* Full logo — single piece shown when doors are still (no edge strip seam).
               Mounts on top of the halves at z-112, hides when sliding begins. */}
          {banner.slideOffset === 0 && !banner.slideTransitioning && (
            <div style={{
              position: 'absolute', left: 3, top: 3,
              width: 964, height: 164,
              zIndex: 112,
              pointerEvents: 'none',
            }}>
              <LogoFull />
            </div>
          )}

          {/* Plus sign rotation wrapper */}
          <div
            style={{
              position: 'absolute', left: 445, top: 45, width: 80, height: 80,
              zIndex: 120,
              pointerEvents: 'none',
              transform: `rotate(${banner.plusRotation}deg)`,
              transition: banner.plusTransitioning
                ? `transform ${ROTATE_DURATION}ms ease` : 'none',
              opacity: banner.plusVisible ? 1 : 0,
            }}
            onTransitionEnd={(e) => {
              if (e.propertyName === 'transform' && e.target === e.currentTarget)
                banner.onPlusTransitionEnd();
            }}
          >
            <PlusSign style={{ left: 0, top: 0 }} />
          </div>
        </div>

        {/* Column Guess Dialog - centered in banner interior, behind logos */}
        {showColumnDialog && (
          <div style={{
            position: 'absolute',
            left: typeof window !== 'undefined' && window.innerWidth < 768 ? 118 : 140,
            top: typeof window !== 'undefined' && window.innerWidth < 768 ? 25 : 40,
            zIndex: 100,
            pointerEvents: isColumnGuessActive ? 'auto' : 'none',
          }}>
            <SubmitDialog
              variant="column"
              onSubmit={(guess) => {
                submitColumnGuess(activeDialogColumn!, activeDialogRow!, guess);
                closeDialog();
              }}
              onBack={closeDialog}
              creditPenalty={activeDialogColumn != null
                ? (guessAttempts[activeDialogColumn] || 0) >= 1 ? 3 : 2
                : undefined}
            />
          </div>
        )}

        {/* Final Answer Dialog - centered in banner interior, behind logos */}
        {showFinalDialog && (
          <div style={{
            position: 'absolute',
            left: typeof window !== 'undefined' && window.innerWidth < 768 ? 118 : 140,
            top: typeof window !== 'undefined' && window.innerWidth < 768 ? 25 : 40,
            zIndex: 100,
            pointerEvents: isFinalAnswerActive ? 'auto' : 'none',
          }}>
            <SubmitDialog
              variant="final"
              onSubmit={(answer) => {
                submitFinalAnswer(answer);
              }}
              onBack={handleFinalBack}
            />
          </div>
        )}

        {/* Main Block - Grid area (15, 200, 970, 620) */}
        <div className={styles.mainBlock}>
          {/* Four column prisms — Y-axis rotation, 3 faces each.
              Geometry from createPrismFromSlot('vert', 232, 620). */}
          {[0, 1, 2, 3].map(columnIndex => (
            <div
              key={columnIndex}
              className={styles.columnWrapper}
              style={{ left: `${COLUMN_X_POSITIONS[columnIndex]}px` }}
            >
              <PrismSlot
                geometry={COL}
                angle={colTargets[columnIndex]}
                transition={(openPhase === 'opening' && colTargets[columnIndex] !== 0) || boardClosing || finalAnswerSubmitted
                  ? `transform ${COL_OPEN_DURATION}ms ease-in-out`
                  : undefined}
                backingColor="#262626"
              >
                {/* Face A: Blank — solid brown (resting state) */}
                <PrismFace angle={0} style={{ backgroundColor: '#514b44' }}>
                </PrismFace>
                {/* Face B: Game / Solution — visible at -120°. */}
                <PrismFace angle={120} style={{
                  backgroundColor: (openGlowCol === columnIndex || openGlowCol === 4) ? '#9a8a70' : '#514b44',
                  transition: 'background-color 250ms ease',
                }}>
                  <ColumnNumberLabel columnIndex={columnIndex as 0|1|2|3} />
                  <div style={{ position: 'absolute', left: 18, top: 74 }}>
                    <ColumnContainer columnIndex={columnIndex} celebrationGoldCol={celebrationCol} bannerDialogVisible={banner.dialogVisible} />
                  </div>
                </PrismFace>
              </PrismSlot>
            </div>
          ))}


        </div>


        {/* Welcome Panel — Play/Resume/Review gate + sample selection, overlays mainBlock */}
        {/* Footer Block - (15, 835, 970, 150) */}
        <div className={styles.footerBlock}>
          {/* Footer-left: CreditCounterArea — stays mounted during boardClosing
              so the AGameOfAnalogies doors close over it. */}
          {(boardOpen || boardClosing) && (
            <div style={{ position: 'absolute', left: 3, top: 3, zIndex: 10 }}>
              <CreditCounterArea
                credits={credits}
                gameWon={gameWon}
                finalScore={finalScore}
                triggerEndGame={triggerEndGameAnim}
                onEndGameAnimComplete={() => setExplanationOpen(true)}
              />
            </div>
          )}

          {/* AGameOfAnalogies — covers footer-left prism when board is closed */}
          <AGameOfAnalogies
            separated={aGameDoorsOpen}
            slideDuration={BOARD_ANIM_DURATION}
            onClosed={() => {}}
            style={{ position: 'absolute', left: 3, top: 3, zIndex: 20 }}
          />
          {/* SolveButtonCell — prism showing solve button (front) or solution (back).
              PRINCIPLE: Mount BEFORE doors start opening, stay mounted UNTIL doors finish closing.
              z-index is always below the doors so it's invisible until doors part. */}
          {doorOpen && (
            <SolveButtonCell
              onSolve={handleSolveClick}
              onClose={() => handleClose()}
              solveActive={isFinalAnswerActive}
              outcomeText={finalAnswerSubmitted && puzzle ? puzzle.finalAnswer.primary : null}
              outcomeCorrect={gameWon !== null ? gameWon : null}
              onRevealComplete={handleRevealComplete}
              triggerReveal={triggerReveal}
              style={{
                position: 'absolute',
                left: 249,
                top: 3,
                zIndex: 2,
              }}
            />
          )}

          {/* Full door — rendered as a backdrop when door halves are at rest (position 0).
              PRINCIPLE: Never show the full door while halves are animating (opening OR closing),
              because it would cover the SolveButtonCell/solution prism behind the halves.
              Only show when:
              - Board is closed (no halves mounted)
              - Opening phase before halves mount (normal game only)
              - doorOpen but halves haven't mounted yet (pre-slide, halves waiting for rAF)
              - doorHalvesClosed=true (halves finished closing, sitting at 0 on top of this) */}
          {((!boardOpen && !boardClosing)
            || (openPhase === 'opening' && !finalAnswerSubmitted)
            || !doorOpen
            || (doorOpen && !doorSlideReady && !boardClosing && doorCancelPhase !== 'closing')
            || doorHalvesClosed) && (
            <RevealDoorFull style={{ zIndex: 3 }} />
          )}

          {/* Split door halves — mounted when doors need to animate apart or back together.
              Each half clips its inner shadow edge so when at translateX(0) they look
              identical to the full door. z-index always above full door AND SolveButtonCell.
              Unmounted only after halves finish closing back to center. */}
          {doorOpen && !doorHalvesClosed && (
            <div style={{
              position: 'absolute',
              left: 247,
              top: 3,
              width: 720,
              height: 144,
              overflow: 'hidden',
              pointerEvents: 'none',
              zIndex: 55,
            }}>
              {/* Left door half — clip right 9px to hide inner shadow bar at rest */}
              <div style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: 360,
                height: 144,
                overflow: 'hidden',
                transform: `translateX(${doorFullyOpen ? -380 : doorSlideReady ? -190 : 0}px)`,
                transition: `transform ${DOOR_SPLIT_DURATION}ms ease`,
              }}
                onTransitionEnd={(e) => {
                  if (e.propertyName === 'transform' && e.target === e.currentTarget) {
                    if (doorFullyOpen && !triggerReveal) {
                      setTimeout(() => setTriggerReveal(true), 300);
                    } else if (!doorFullyOpen && !doorSlideReady && doorCancelPhase === 'closing') {
                      setDoorCancelPhase('closed');
                    } else if (!doorFullyOpen && !doorSlideReady && boardClosing) {
                      // Halves finished closing — swap to full door
                      setDoorHalvesClosed(true);
                    }
                  }
                }}
              >
                <SvgRevealDoorLeft style={{ position: 'absolute', left: 0, top: 0, width: 369, height: 144 }} />
              </div>
              {/* Right door half — clip left 9px to hide inner shadow bar at rest */}
              <div style={{
                position: 'absolute',
                left: 360,
                top: 0,
                width: 360,
                height: 144,
                overflow: 'hidden',
                transform: `translateX(${doorFullyOpen ? 380 : doorSlideReady ? 190 : 0}px)`,
                transition: `transform ${DOOR_SPLIT_DURATION}ms ease`,
              }}>
                <div style={{ position: 'relative', left: -9, width: 369, height: 144 }}>
                  <SvgRevealDoorRight style={{ position: 'absolute', left: 0, top: 0, width: 369, height: 144 }} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* WelcomePanel — rendered OUTSIDE the scaled board for crisp text.
          Positioned in viewport coordinates relative to the outer fixed div. */}
      {showWelcomePanel && puzzle && (
        <WelcomePanel
          selectedPuzzle={sampleIndex !== undefined ? {
            id: '', name: puzzleName ?? '', puzzleText: '', date: puzzle.date, sampleIndex
          } : null}
          puzzleDisplayName={formatPuzzleDisplayName(puzzleName, sampleIndex, puzzle.date)}
          onStart={handleStart}
          onSelectPuzzle={onSelectPuzzle ?? (() => {})}
          onReset={handleReset}
          risePhase={risePhase}
          boardScale={scale}
        />
      )}
    </div>
    </>
  );
}

export default Board;
